<script lang="ts" setup>
import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { Heading, Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';

interface Props {
  requested: boolean;
  requesting: boolean;
  error: string | null;
  schoolName: string;
}

const props = defineProps<Props>();

defineEmits<{
  request: [];
}>();

const { t } = useI18n();

const sentMessage = ref<HTMLElement | null>(null);

watch(
  () => props.requested,
  async (requested) => {
    if (!requested) {
      return;
    }
    await nextTick();
    sentMessage.value?.focus();
  },
);
</script>

<template>
  <KUICoreCard
    rounding="3xl"
    padding="md"
    elevation="none"
    omit-background
    class="bg-linear-to-br from-brand-yellow-subtle via-brand-orange-subtle to-background"
    data-testid="schools-verification-cta"
  >
    <div
      v-if="requested"
      ref="sentMessage"
      tabindex="-1"
      role="status"
      class="flex items-start gap-2 outline-none"
      data-testid="schools-verification-cta-sent"
    >
      <Icon :name="ICONS.checkmarkCircle" class="mt-0.5 shrink-0" />
      <Text as="span" size="sm">
        {{ t('schools.verification.sent') }}
      </Text>
    </div>
    <div v-else class="flex flex-col items-start gap-3">
      <Heading
        size="xs"
        as="h2"
        class="leading-tight"
      >
        {{ t('schools.verification.title') }}
      </Heading>
      <Text size="sm">
        {{ t('schools.verification.body', { schoolName }) }}
      </Text>
      <Button
        size="sm"
        type="button"
        class="mt-2"
        data-testid="schools-verification-cta-button"
        :loading="requesting"
        @click="$emit('request')"
      >
        {{ t('schools.verification.action') }}
      </Button>
      <Text
        v-if="error"
        size="sm"
        class="text-destructive"
        role="alert"
        data-testid="schools-verification-cta-error"
      >
        {{ error }}
      </Text>
    </div>
  </KUICoreCard>
</template>
